const CSS_MODULES = import.meta.env?.CSS_MODULES;

export const CSS_MODULE_SCRIPTS =
	CSS_MODULES ||
	(await import("~/index.css", { with: { type: "css" } }).then(() => true).catch(() => false));

/** @param {string} path @param {string} base */
export async function importCss(path, base) {
	const url = new URL(path, base);
	const sheet = new CSSStyleSheet({ baseURL: url.href });
	const res = await fetch(url);

	await sheet.replace(await res.text());

	return { default: sheet };
}

/** Integer to be appended to each class name. */
let suffix = 0;

/** @type {WeakSet<CSSStyleSheet>} A set storing the imported stylesheets. */
const imported = new WeakSet();

/** Regex matching class selectors. */
const CLASSNAMES = /\.-?[_a-zA-Z]+[_a-zA-Z0-9-]*/g;

/**
 * Apply a `CSSStyleSheet` instance to the document, modularizing any class names and returning a
 * mapping between human readable class names and modularized ones.
 *
 * For example, calling `adopt` on a stylesheet containing the following CSS:
 *
 * ```css
 * .foo {
 * 	color: red;
 * }
 * .bar {
 * 	color: blue;
 * }
 * ```
 *
 * Might end up applying the following to the document:
 *
 * ```css
 * .foo_1 {
 * 	color: red;
 * }
 * .bar_1 {
 * 	color: blue;
 * }
 * ```
 *
 * And return the following JavaScript object:
 *
 * ```json
 * { "foo": "foo_1", "bar": "bar_1" }
 * ```
 *
 * @param {CSSStyleSheet} sheet The stylesheet to adopt
 * @returns {Record<string, string>}
 */
export function adopt(sheet) {
	// if this has been bundled, `sheet` should already be the class name map —
	// despite the type signature — so we just return it immediately.
	// "defining" `import.meta.CSS_MODULES` as `true` in the bundler configuration
	// should allow the rest of this function (and file) to be omitted from the bundle
	// @ts-expect-error
	if (CSS_MODULES) return sheet;

	// ensure the stylesheet hasn't already been imported
	if (imported.has(sheet)) throw new Error("Stylesheet imported twice!");
	imported.add(sheet);

	// modularize the class names in the stylesheet and obtain a map of the class names
	const classnames = modularize(sheet.cssRules, ++suffix);

	// add the stylesheet to the document
	document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];

	// return the map of class names
	return new Proxy(classnames, {
		get(target, prop, reciever) {
			if (!Object.hasOwn(target, prop)) console.warn(`Undefined class name: .${String(prop)}`);
			return Reflect.get(target, prop, reciever);
		},
	});
}

/**
 * Recursively modularize the class names of a `CSSRuleList`, modifying the rules in the original
 * stylesheet and collecting a map of human readable names to modularized names.
 *
 * @param {CSSRuleList} rules A list of CSS rules to modularize
 * @param {number} id An ID to append to the rules
 */
function modularize(rules, id) {
	/** @type {Record<string, string>} The map of human readable names to "modularized" names */
	const map = {};

	for (const rule of rules) {
		// // if there are nested CSS rules (e.g. in a nested selector) recursively modularize them
		if ("cssRules" in rule && rule.cssRules instanceof CSSRuleList) {
			Object.assign(map, modularize(rule.cssRules, id));
		}

		// if _this_ rule is not a CSSStuleRule, it can't be modularized
		if (!(rule instanceof CSSStyleRule)) continue;

		// get all classnames in the selector, sorting from longest to shortest
		const classnames = rule.selectorText.match(CLASSNAMES)?.sort((a, b) => b.length - a.length);

		// for each unique class name in the selector…
		for (const classname of new Set(classnames)) {
			// create a "modularized" class name with the original name and ID
			const modularized = `._${classname.slice(1)}_${id}`;

			// map the human readable class name to the modularized one (without the leading dots)
			map[classname.slice(1)] = modularized.slice(1);

			// update the selector text to use the modularized class name
			rule.selectorText = rule.selectorText.replaceAll(classname, modularized);
		}
	}

	// return the map of class names
	return map;
}
